import { useCallback, useEffect, useRef, useState } from 'react';
import { throttle } from 'lodash';

type RootRefType = typeof window | null;

interface UseFloatingTopButton {
  handleIsShowTopButton: (value: boolean) => void;
}

const useFloatingTopButton = ({
  handleIsShowTopButton,
}: UseFloatingTopButton) => {
  const rootRef = useRef<RootRefType>(null);
  const [scrollY, setScrollY] = useState<number>(0);

  const handleClickScrollTop = useCallback(() => {
    if (!rootRef?.current) return;

    rootRef.current.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  const handleWatchScroll = throttle(() => {
    const currentScrollY = rootRef?.current?.scrollY ?? 0;

    setScrollY(currentScrollY);
  }, 300);

  const handleChangeButtonExposure = useCallback(() => {
    const exposurePoint = window?.innerHeight / 3;
    handleIsShowTopButton(scrollY > exposurePoint);
  }, [handleIsShowTopButton, scrollY]);

  const initScrollEvent = () => {
    rootRef.current = window;
    rootRef.current.addEventListener('scroll', handleWatchScroll);
  };

  const destroyScrollEvent = () => {
    if (rootRef?.current === null) return;
    rootRef.current.removeEventListener('scroll', handleWatchScroll);
  };

  useEffect(() => {
    initScrollEvent();

    return () => {
      destroyScrollEvent();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handleChangeButtonExposure();
  }, [handleChangeButtonExposure]);

  return { handleClickScrollTop };
};

export default useFloatingTopButton;
